import React, { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";

// hooks
import useAuth from "hooks/useAuth";
import useStockTable from "./hooks/useStockTable";
import useCategoriesTable from "./hooks/useCategoriesTable";

// reusable components
import { SecureTableShell } from "@/components/ui/SecureTableShell";
import EmptyStateCard from "@/components/ui/EmptyStateCard";
import PillTabs from "@/components/ui/PillTabs";
import StockSearchControls from "./components/StockSearchControls";
import CategorySearchControls from "./components/CategorySearchControls";
import InventorySummary from "./components/InventorySummary";

// utils
import { getFallbackStateInventory } from "./utils/getFallbackStateInventory";

const StockTab = () => {
  const {
    allItems,
    items: filteredItems,
    categories,
    searchTerm,
    updateSearchTerm,
    categoryFilter,
    updateCategoryFilter,
    statusFilter,
    updateStatusFilter,
    stockColumns,
    renderStockRow,
    isLoading,
    isError,
    refetch,
  } = useStockTable();

  const [rawSearchTerm, setRawSearchTerm] = useState(searchTerm);
  useEffect(() => {
    const timeout = setTimeout(() => updateSearchTerm(rawSearchTerm), 600);
    return () => clearTimeout(timeout);
  }, [rawSearchTerm, updateSearchTerm]);

  const fallback = getFallbackStateInventory({ isLoading, allItems, filteredItems });

  if (isError) {
    return (
      <EmptyStateCard
        title="Failed to load stock"
        description="Please try again later."
        buttonLabel="Retry"
        onAction={refetch}
      />
    );
  }

  return (
    <>
      <InventorySummary items={allItems} />
      <StockSearchControls
        searchTerm={rawSearchTerm}
        updateSearchTerm={setRawSearchTerm}
        categoryFilter={categoryFilter}
        updateCategoryFilter={updateCategoryFilter}
        statusFilter={statusFilter}
        updateStatusFilter={updateStatusFilter}
        categories={categories}
      />
      <SecureTableShell
        data={filteredItems}
        columns={stockColumns}
        renderRow={renderStockRow}
        fallbackState={fallback.state}
        fallbackMessages={{
          noData: {
            title: "No stock records",
            description: "Stock levels appear here once products are added.",
            buttonLabel: null,
          },
          noMatch: {
            title: "No matching stock",
            description: "Try adjusting your search or filters.",
            buttonLabel: null,
          },
        }}
      />
    </>
  );
};

const CategoriesTab = () => {
  const [rawSearchTerm, setRawSearchTerm] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(rawSearchTerm), 400);
    return () => clearTimeout(timeout);
  }, [rawSearchTerm]);

  const {
    allCategories,
    categories: filteredCategories,
    categoryColumns,
    renderCategoryRow,
    isLoading,
    isError,
    refetch,
  } = useCategoriesTable(debounced);

  const fallback = getFallbackStateInventory({
    isLoading,
    allItems: allCategories,
    filteredItems: filteredCategories,
  });

  if (isError) {
    return (
      <EmptyStateCard
        title="Failed to load categories"
        description="Please try again later."
        buttonLabel="Retry"
        onAction={refetch}
      />
    );
  }

  return (
    <>
      <CategorySearchControls searchTerm={rawSearchTerm} updateSearchTerm={setRawSearchTerm} />
      <SecureTableShell
        data={filteredCategories}
        columns={categoryColumns}
        renderRow={renderCategoryRow}
        fallbackState={fallback.state}
        fallbackMessages={{
          noData: {
            title: "No categories yet",
            description: "Group your products by creating categories.",
            buttonLabel: null,
          },
          noMatch: {
            title: "No matching categories",
            description: "Try a different search term.",
            buttonLabel: null,
          },
        }}
      />
    </>
  );
};

const TABS = [
  { id: "stock", label: "Stock" },
  { id: "categories", label: "Categories" },
];

const Inventory = () => {
  const { isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") === "categories" ? "categories" : "stock";

  const setActiveTab = (id) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", id);
    // clear filters that don't apply across tabs
    params.delete("search");
    params.delete("category");
    params.delete("stockStatus");
    setSearchParams(params);
  };

  if (!isAuthenticated) {
    return <Navigate to="/auth/sign-in" replace />;
  }

  return (
    <div>
      <PillTabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

      {activeTab === "stock" ? <StockTab /> : <CategoriesTab />}
    </div>
  );
};

export default Inventory;
